import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { registrarCentro, obtenerCentro, editarCentro } from '../../services/centroService';
import { Save, X, PlusCircle, Building2, Landmark, MapPin, Globe, ListChecks, Radar } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Bancos from './bancos';

// Funciones de notificación estandarizadas
const showSuccessToast = (message) => {
    toast.success(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'centro'
    });
};

const showErrorToast = (message) => {
    toast.error(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'centro'
    });
};

const showWarningToast = (message) => {
    toast.warning(message, {
        position: 'top-right',
        autoClose: 3000,
        style: { 
            marginTop: '10px',
            fontFamily: '"Exo 2", sans-serif',
            fontWeight: 500
        },
        containerId: 'centro'
    });
};

Modal.setAppElement('#root');

const GestionCentro = () => {
  const [centro, setCentro] = useState(null);
  const [formData, setFormData] = useState({
    codigo_unidad: '',
    nombre_unidad: '',
    provincia: '',
    canton: '',
    parroquia: '',
    distrito: '',
    zona:''
  });
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');

  const cargarCentro = async () => {
    setLoading(true);
    try {
      const response = await obtenerCentro();
      
      if (response.success && response.data) {
        // Si la respuesta es un array, tomar el primer elemento
        const data = Array.isArray(response.data) ? response.data[0] : response.data;
        
        if (data) {
          setCentro(data);
          setFormData({
            codigo_unidad: data.codigo_unidad || '',
            nombre_unidad: data.nombre_unidad || '',
            provincia: data.provincia || '',
            canton: data.canton || '',
            parroquia: data.parroquia || '',
            distrito: data.distrito || '',
            zona: data.zona || ''
          });
        } else {
          console.log('No se encontraron datos de centro en la respuesta');
        }
      } else {
        console.log('No se encontró centro o la respuesta no fue exitosa:', response);
      }
    } catch (error) {
      console.error('Error al cargar el centro:', error);
      showErrorToast('Error al cargar el centro: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarCentro();
  }, []);

  const manejarCambio = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const manejarSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (modalTitle === 'Registrar Centro') {
        const response = await registrarCentro(formData);
        if (response && response.data) {
          setCentro({ ...formData, id_centro: response.data.id_centro });
          showSuccessToast('Centro registrado exitosamente');
          setIsModalOpen(false);
        } else {
          showErrorToast('Error al registrar el centro');
        }
      } else {
        const response = await editarCentro(centro.id_centro, formData);
        if (response && response.data) {
          setCentro({ ...formData, id_centro: centro.id_centro });
          showSuccessToast('Centro actualizado exitosamente');
          setIsModalOpen(false);
        } else {
          showErrorToast('Error al actualizar el centro');
        }
      }
    } catch (error) {
      showErrorToast(error.message || 'Error al guardar los datos del centro');
    } finally {
      setLoading(false);
    }
  };

  const abrirModalRegistro = () => {
    setModalTitle('Registrar Centro');
    setFormData({
      codigo_unidad: '',
      nombre_unidad: '',
      provincia: '',
      canton: '',
      parroquia: '',
      distrito: '',
      zona:''
    });
    setIsModalOpen(true);
  };

  const abrirModalEdicion = () => {
    setModalTitle('Editar Centro');
    setFormData({
      codigo_unidad: centro.codigo_unidad || '',
      nombre_unidad: centro.nombre_unidad || '',
      provincia: centro.provincia || '',
      canton: centro.canton || '',
      parroquia: centro.parroquia || '',
      distrito: centro.distrito || '',
      zona: centro.zona || ''
    });
    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-white rounded-xl p-2 sm:p-4 shadow">
      <ToastContainer 
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        className="mt-16"
        limit={3}
        enableMultiContainer
        containerId="centro"
        style={{ zIndex: 9999 }}
      />
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
          <div className="flex items-center">
            <Building2 className="h-8 w-8 text-indigo-500 mr-3" />
            <div>
              <h1 className="text-xl sm:text-2xl font-medium text-gray-900">Gestión del Centro</h1>
              <p className="text-sm text-gray-500">Administre la información de su unidad educativa</p>
            </div>
          </div>
          {!centro && (
            <Button onClick={abrirModalRegistro}>
              <PlusCircle className="mr-2" size={20} />
              Registrar Centro
            </Button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
          </div>
        ) : centro ? (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 sm:gap-0">
                  <h2 className="text-lg font-medium text-gray-900">Información del Centro</h2>
                  <Button onClick={abrirModalEdicion} variant="primary" size="sm">
                    <Save className="mr-2" size={16} />
                    Editar
                  </Button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Código AMIE', value: centro.codigo_unidad, icon: Globe },
                    { label: 'Nombre', value: centro.nombre_unidad, icon: Building2 },
                    { label: 'Provincia', value: centro.provincia, icon: MapPin },
                    { label: 'Cantón', value: centro.canton, icon: MapPin },
                    { label: 'Parroquia', value: centro.parroquia, icon: MapPin },
                    { label: 'Distrito', value: centro.distrito, icon: MapPin },
                    { label: 'Zona', value: centro.zona, icon: Radar }
                  ].map(({ label, value, icon: Icon }) => (
                    <div key={label} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 text-indigo-500 mr-2" />
                        <div>
                          <p className="text-xs text-gray-500">{label}</p>
                          <p className="text-sm font-medium text-gray-900">{value || (label === 'Código AMIE' ? 'Código AMIE no registrado' : 'No registrado')}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow overflow-hidden p-4 sm:p-6">
              <Bancos />
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 text-center">
            <Building2 className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay información registrada</h3>
            <p className="text-gray-500 mb-4">Para comenzar, registre su centro educativo</p>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => setIsModalOpen(false)}
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4"
        overlayClassName="fixed inset-0 bg-black/40 backdrop-blur-sm"
        contentLabel={modalTitle}
        ariaHideApp={false}
      >
        <div className="bg-white rounded-2xl shadow-xl p-3 sm:p-6 w-full max-w-lg mx-auto">
          <div className="flex justify-between items-center mb-3 sm:mb-6">
            <h2 className="text-base sm:text-xl font-medium text-gray-900 flex items-center">
              <Building2 className="mr-2 sm:mr-3 h-5 w-5 sm:h-6 sm:w-6 text-indigo-500" />
              {modalTitle}
            </h2>
            <Button 
              onClick={() => setIsModalOpen(false)}
              variant="ghost"
              size="icon"
              className="hover:bg-gray-100 rounded-full"
            >
              <X size={18} />
            </Button>
          </div>

          <form onSubmit={manejarSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              {[
                { name: 'codigo_unidad', label: 'Código AMIE', type: 'text' },
                { name: 'nombre_unidad', label: 'Nombre', type: 'text' },
                { name: 'provincia', label: 'Provincia', type: 'text' },
                { name: 'canton', label: 'Cantón', type: 'text' },
                { name: 'parroquia', label: 'Parroquia', type: 'text' },
                { name: 'distrito', label: 'Distrito', type: 'text' },
                { name: 'zona', label: 'Zona', type: 'text' }
              ].map(({ name, label, type }) => (
                <div key={name}>
                  <label className="block text-xs sm:text-sm font-medium text-gray-900 mb-1">
                    {label}
                  </label>
                  <input
                    type={type}
                    name={name}
                    value={formData[name]}
                    onChange={manejarCambio}
                    className="w-full rounded-lg border border-gray-300 bg-white py-1.5 sm:py-2 px-2 sm:px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    required={name !== 'codigo_unidad'}
                  />
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 mt-3 sm:mt-6">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setIsModalOpen(false)}
                className="w-full sm:w-auto text-xs sm:text-sm py-1.5 sm:py-2"
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                variant="primary"
                className="w-full sm:w-auto text-xs sm:text-sm py-1.5 sm:py-2"
              >
                <Save className="mr-2 h-4 w-4" />
                {modalTitle === 'Registrar Centro' ? 'Registrar' : 'Guardar cambios'}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
};

const Button = ({ children, onClick, className = '', variant = 'primary', size = 'default', type }) => {
  const baseStyles = 'rounded-lg font-medium transition-all duration-300 flex items-center justify-center shadow-sm';
  
  const variantStyles = {
    primary: 'bg-indigo-500 text-white hover:bg-indigo-600',
    ghost: 'bg-transparent text-gray-600 hover:bg-gray-50',
    green: 'bg-emerald-500 text-white hover:bg-emerald-600',
    danger: 'bg-rose-500 text-white hover:bg-rose-600'
  };

  const sizeStyles = {
    default: 'px-4 py-2 text-sm',
    sm: 'px-3 py-1.5 text-xs',
    lg: 'px-6 py-3 text-base',
    icon: 'p-2',
    xs: 'p-1 text-xs'
  };

  return (
    <button
      type={type}
      onClick={onClick}
      className={`
        ${baseStyles} 
        ${variantStyles[variant]} 
        ${sizeStyles[size]} 
        ${className}
      `}
    >
      {children}
    </button>
  );
};

export default GestionCentro;